<template>
  <div class="min-h-screen bg-lightGray">
    <div class="mx-auto p-4">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-2xl text-gray-800">Tuition Management</h1>
        <p class="mt-1 text-sm text-muted-foreground">Manage your monthly tuition, payment methods, and billing</p>
      </div>

      <!-- Retry Payment Success Message -->
      <div v-if="retryPaymentSuccess" class="mb-4 flex items-center gap-3 bg-white border border-border rounded-lg px-4 py-3">
        <div class="flex h-7 w-7 items-center justify-center rounded-full bg-green-100 shrink-0">
          <Check class="w-4 h-4 text-green-600" />
        </div>
        <div class="flex-1">
          <p class="text-sm font-medium text-gray-900">Payment Successful!</p>
          <p class="text-xs text-muted-foreground">Your payment has been processed. Your monthly tuition is now active.</p>
        </div>
        <button @click="retryPaymentSuccess = false" class="text-muted-foreground hover:text-gray-600 transition-colors">
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- Content -->
      <div class="space-y-4">
        <ProductAccessBanner />

        <!-- Static Header Sections - Never re-render -->
        <div class="static-sections space-y-4">
          <SubscriptionStatus
            :subscription="staticSubscriptionData.subscription"
            :assigned-plan="staticSubscriptionData.assignedPlan"
            :is-loading="subscriptionsStore.loading"
            :action-loading="isSubscriptionActionLoading"
            :action-error="subscriptionActionError"
            @subscribe="startSubscriptionCheckout"
            @restore-subscription="handleRestoreSubscription"
            @reactivate-subscription="handleReactivateSubscription"
            @retry-payment="handleRetryPayment"
          />

          <PaymentMethodsSection
            :payment-methods="staticSubscriptionData.paymentMethods"
            :is-loading="staticSubscriptionData.paymentMethodsLoading"
            :is-processing-checkout="isProcessingCheckout"
            :checkout-success="checkoutSuccess"
            :checkout-error="checkoutError"
            @payment-method-updated="paymentMethodsStore.fetchPaymentMethods"
            @update:checkout-error="checkoutError = $event"
          />
        </div>

        <!-- Dynamic Billing History Section - Can re-render independently -->
        <BillingHistorySection
          :payments="billingRows"
          :is-loading="isPaymentsLoading"
          :error="paymentsStore.error"
          :has-payments="hasPayments"
          :pagination="paymentsStore.pagination"
          :total-count="paymentsStore.totalCount || 0"
          :all-count="paymentsStore.allCount || 0"
          :status-counts="paymentsStore.statusCounts"
          :selected-status="selectedStatus"
          @filters-change="handleFiltersChange"
          @page-change="handlePageChange"
          @retry="() => paymentsStore.fetchPayments()"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.static-sections {
  /* These sections should never re-render */
  contain: layout style;
}
</style>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { 
  X,
  Check
} from 'lucide-vue-next'
import { 
  usePlansStore,
  usePaymentMethodsStore,
  useSubscriptionsStore,
  usePaymentsStore,
  initializeSubscriptionData,
  initializePaymentsData
} from '@/stores/subscription'
import { useSubscriptionActions } from '@/composables/useSubscriptionActions'
import SubscriptionStatus from '@/components/subscription/SubscriptionStatus/index.vue'
import ProductAccessBanner from '@/components/subscription/ProductAccessBanner.vue'
import PaymentMethodsSection from '@/components/subscription/PaymentMethod/PaymentMethodsSection.vue'
import BillingHistorySection from '@/components/subscription/BillingHistorySection.vue'
import type {
  Subscription,
  SubscriptionPayment,
  ProductPurchase,
  PaymentFilters as PaymentFiltersType,
  PaymentStatus
} from '@/types/subscription'
import { subscriptionService } from '@/services/subscriptionService'
import { APP_ROUTES } from '@/const/appRoutes'

// Subscription statuses that require different handling
const RETRY_PAYMENT_STATUSES: Array<Subscription['status']> = [
  'past_due',
  'incomplete',
  'unpaid'
]

const REDIRECT_TO_SUBSCRIPTION_STATUSES: Array<Subscription['status']> = [
  'canceled',
  'incomplete_expired'
]

const isRetryPaymentContext = (subscription: Subscription | null | undefined) =>
  !!subscription && RETRY_PAYMENT_STATUSES.includes(subscription.status)

const shouldRedirectToSubscriptionPage = (subscription: Subscription | null | undefined) =>
  !!subscription && REDIRECT_TO_SUBSCRIPTION_STATUSES.includes(subscription.status)

// Extended filters type to include status
type ExtendedPaymentFilters = PaymentFiltersType & {
  status?: PaymentStatus | 'all'
}

const router = useRouter()
const route = useRoute()
const plansStore = usePlansStore()
const paymentMethodsStore = usePaymentMethodsStore()
const subscriptionsStore = useSubscriptionsStore()
const paymentsStore = usePaymentsStore()

const {
  isSubscriptionActionLoading,
  subscriptionActionError,
  handleReactivateSubscription: reactivateSubscription,
  handleRetryPayment: retryPayment,
  createSubscriptionCheckout,
  extractErrorMessage
} = useSubscriptionActions()

// State
const selectedStatus = ref<PaymentStatus | 'all'>('all')
const isProcessingCheckout = ref(false)
const checkoutError = ref<string | null>(null)
const checkoutSuccess = ref(false)
const retryPaymentSuccess = ref(false)

// Product purchases (one-time packages) shown alongside subscription payments
const productPurchases = ref<ProductPurchase[]>([])

const fetchProductPurchases = async () => {
  try {
    const response = await subscriptionService.getMyProductPurchases()
    productPurchases.value = response.data
  } catch {
    productPurchases.value = []
  }
}

// Purchases mapped into billing-history rows. Only rendered on the first page
// (payments are server-paginated; purchases are few and recent, so this keeps
// them from repeating on every page). Negative ids avoid key collisions.
const productPurchaseRows = computed<SubscriptionPayment[]>(() => {
  const currentPage = paymentsStore.pagination?.current_page ?? 1
  if (currentPage !== 1) return []
  return productPurchases.value
    .filter((p) => selectedStatus.value === 'all' || p.status === selectedStatus.value)
    .map((p) => ({
      id: -p.id,
      subscription: 0,
      subscription_plan_name: p.product_name || p.product?.name || 'Package',
      stripe_payment_intent_id: p.stripe_payment_intent_id || '',
      amount: Number(p.amount),
      formatted_amount: `$${Number(p.amount).toFixed(2)}`,
      currency: p.product?.currency || 'usd',
      status: p.status as PaymentStatus,
      payment_date: p.created_at || p.starts_at || ''
    }))
})

const billingRows = computed<SubscriptionPayment[]>(() =>
  [...productPurchaseRows.value, ...paymentsStore.payments].sort(
    (a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
  )
)

// Computed
const hasPayments = computed(() => billingRows.value.length > 0)

const isPaymentsLoading = computed(() => paymentsStore.loading)

const primarySubscription = computed(() => {
  return subscriptionsStore.subscriptions[0] || subscriptionsStore.activeSubscription || null
})

const assignedPlan = computed(() => plansStore.plans[0] || null)

// Static data that should never change during payments operations
const staticSubscriptionData = computed(() => ({
  subscription: primarySubscription.value,
  assignedPlan: assignedPlan.value,
  paymentMethods: paymentMethodsStore.paymentMethods,
  paymentMethodsLoading: paymentMethodsStore.loading
}))

// Methods
const refreshData = async () => {
  await Promise.all([
    initializeSubscriptionData(),
    initializePaymentsData(),
    fetchProductPurchases()
  ])
}

const handleError = (message: string) => {
  checkoutError.value = message
}

// Handle Stripe Checkout return for payment method and retry payment
const clearQueryParams = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.replace({ query: {} })
}

const handleCheckoutReturn = async () => {
  const sessionId = route.query.session_id as string | undefined
  const cancelled = route.query.cancelled

  if (cancelled === 'true') {
    // Determine if it was retry payment or add card based on subscription status
    const subscription = primarySubscription.value
    const inRetryContext = isRetryPaymentContext(subscription)
    
    if (inRetryContext) {
      // Retry Payment was cancelled
      subscriptionActionError.value = 'Payment was cancelled. Please try again when ready.'
    } else {
      // Add Card was cancelled
      checkoutError.value = 'Adding payment method was cancelled.'
    }
    clearQueryParams()
    return
  }

  if (!sessionId) return

  isProcessingCheckout.value = true

  try {
    const result = await subscriptionsStore.retrieveCheckoutSession(sessionId)

    if (result.status === 'complete') {
      // Check the mode to determine if it's retry payment (mode: 'payment') or add card (mode: 'setup')
      const isRetryPayment = result.mode === 'payment'
      
      if (isRetryPayment) {
        // Retry Payment success
        retryPaymentSuccess.value = true
        subscriptionActionError.value = null
        
        setTimeout(() => {
          retryPaymentSuccess.value = false
        }, 5000)
      } else {
        // Add Card success
        checkoutSuccess.value = true
        
        setTimeout(() => {
          checkoutSuccess.value = false
        }, 3000)
      }

      clearQueryParams()
      await refreshData()
    } else {
      // Determine error location based on mode if available
      const isRetryPayment = result.mode === 'payment'
      if (isRetryPayment) {
        subscriptionActionError.value = 'Checkout session is not complete. Please contact support.'
      } else {
        checkoutError.value = 'Checkout session is not complete. Please contact support.'
      }
    }
  } catch (error) {
    // Try to determine error location based on subscription status context
    const errorMessage = extractErrorMessage(error, 'Failed to process checkout session')
    const subscription = primarySubscription.value
    const inRetryContext = isRetryPaymentContext(subscription)
    
    if (inRetryContext) {
      // Likely retry payment error
      subscriptionActionError.value = errorMessage
    } else {
      // Likely add card error
      checkoutError.value = errorMessage
    }
    clearQueryParams()
  } finally {
    isProcessingCheckout.value = false
  }
}

const handleReactivateSubscription = async (_subscription: Subscription) => {
  try {
    await reactivateSubscription()
    await refreshData()
  } catch (error) {
    handleError(extractErrorMessage(error, 'Failed to reactivate subscription'))
  }
}

const handleRestoreSubscription = async (_subscription: Subscription) => {
  await startSubscriptionCheckout()
}

const handleRetryPayment = async (_subscription: Subscription) => {
  await retryPayment()
}

const startSubscriptionCheckout = async () => {
  const subscription = primarySubscription.value

  // Redirect to subscription page if no subscription or status requires new subscription
  if (!subscription || shouldRedirectToSubscriptionPage(subscription)) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push(APP_ROUTES.TUITION)
    return
  }

  const planId = assignedPlan.value?.id

  if (!planId) {
    subscriptionActionError.value = 'No subscription plan available to restart checkout.'
    return
  }

  await createSubscriptionCheckout(planId)
}

// Payment pagination and filtering methods
const handlePageChange = async (page: number) => {
  await paymentsStore.goToPage(page)
}

const handleFiltersChange = async (filters: ExtendedPaymentFilters) => {
  if (filters.status !== undefined) {
    selectedStatus.value = filters.status
  }
  await paymentsStore.filterPayments(filters)
}

// Lifecycle
onMounted(async () => {
  await refreshData()
  await handleCheckoutReturn()
})
</script>
